import { useEffect, useRef, useState } from 'preact/hooks';
import type { LastLogin, OAuthProvider } from '../../../core/auth';
import type { LayoutBlock } from '../../../core/types';
import { PaywallError } from '../../../core/types';
import type { BlockProps } from '../types';
import { useI18n, type TFn } from '../../i18n';

type AuthPanelBlock = Extract<LayoutBlock, { type: 'auth_panel' }>;

// `otp` — email entry for the passwordless code flow; `otp_verify` — the
// 6-digit code entry that follows. Both live entirely in this panel, so
// verifyOtp mints the session on the host origin — unlike the signup link flow,
// whose confirmation happens on the paywall's custom domain and can't cross
// back (see the signup_sent auto-resume effect).
type Mode =
  | 'signin'
  | 'signup'
  | 'signup_sent'
  | 'forgot'
  | 'reset_sent'
  | 'reset_verify'
  | 'otp'
  | 'otp_verify';

function providerLabel(provider: OAuthProvider, t: TFn): string {
  switch (provider) {
    case 'google':
      return t('auth.continue_with_google', 'Continue with Google');
    case 'apple':
      return t('auth.continue_with_apple', 'Continue with Apple');
    case 'github':
      return t('auth.continue_with_github', 'Continue with GitHub');
    case 'facebook':
      return t('auth.continue_with_facebook', 'Continue with Facebook');
  }
}

// `err.message` from ApiClient on backend errors without a `message` field = HTTP statusText
// ("Unauthorized", "Bad Request") — English and raw. We map stable
// `err.code` values to i18n keys; for anything unrecognized — a generic fallback instead of
// statusText. `code` comes from the response body (`payload.code`), or
// `http_<status>`, or `network_error` (see api.ts).
function authErrorMessage(
  err: unknown,
  mode: 'signin' | 'signup' | 'otp' | 'reset',
  t: TFn
): string {
  const fallback =
    mode === 'signup'
      ? t('auth.signup_failed', 'Sign-up failed')
      : t('auth.signin_failed', 'Sign-in failed');
  if (!(err instanceof PaywallError)) return fallback;
  switch (err.code) {
    case 'invalid_credentials':
      return t('auth.invalid_credentials', 'Invalid email or password');
    case 'email_not_confirmed':
      return t('auth.email_not_confirmed', 'Please confirm your email before signing in.');
    case 'email_exists':
    case 'user_already_exists':
      return t('auth.email_exists', 'An account with this email already exists.');
    case 'weak_password':
      return t('auth.weak_password', 'Password is too weak.');
    case 'invalid_otp':
    case 'otp_expired':
    case 'token_expired':
      return t('auth.invalid_otp', 'The code is invalid or has expired.');
    case 'over_email_send_rate_limit':
    case 'over_request_rate_limit':
    case 'rate_limited':
    case 'http_429':
      return t('auth.rate_limited', 'Too many requests. Please try again later.');
    case 'network_error':
      return t('auth.network_error', 'Network error. Please check your connection and try again.');
    case 'upstream':
    case 'upstream_error':
    case 'http_502':
    case 'http_503':
    case 'http_504':
      return t('auth.service_unavailable', 'Service is temporarily unavailable. Please try again.');
    // Merchant misconfiguration, not a user error: OAuth redirects and
    // confirmation/recovery email links are built from the paywall's custom
    // domain (backend hard-fails without it). Shown as-is so the integrating
    // developer sees the actionable cause right in the UI during setup.
    case 'custom_domain_required':
      return t(
        'auth.custom_domain_required',
        'Sign-in is not available: the paywall has no custom domain configured (platform settings → Custom domains).'
      );
    default:
      return fallback;
  }
}

export function AuthPanel({ block, ctx }: BlockProps<AuthPanelBlock>) {
  const auth = ctx.auth;
  const session = ctx.authSession;
  const allowSignup = block.allow_signup !== false;
  const allowReset = block.allow_password_reset !== false;
  // Opt-in, not opt-out: a second sign-in route next to the password field makes
  // the form busier for every paywall that never asked for it.
  const allowEmailCode = block.allow_email_code === true;
  const hideWhenAuthed = block.hide_when_authenticated !== false;

  if (!auth) {
    if (typeof console !== 'undefined') {
      console.warn('[paywall] auth_panel rendered without AuthClient — pass `auth: true` to PaywallUI');
    }
    return null;
  }

  // An anonymous session means "not authenticated": anon is only good for the api-gateway,
  // purchase/restore require a real signin.
  const realSession = session && !session.user.is_anonymous ? session : null;
  if (realSession && hideWhenAuthed) return null;

  if (realSession) {
    return <SignedIn email={realSession.user.email ?? ''} onSignOut={() => auth.signOut().catch(() => {})} />;
  }

  return (
    <AuthForm
      block={block}
      allowSignup={allowSignup}
      allowReset={allowReset}
      allowEmailCode={allowEmailCode}
      ctx={ctx}
    />
  );
}

function SignedIn({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const { t } = useI18n();
  return (
    <div class="flex items-center justify-between gap-3 rounded-2xl bg-gray-100 px-4 py-3">
      <div class="flex flex-col">
        <span class="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          {t('auth.signed_in', 'Signed in')}
        </span>
        <span class="text-sm font-medium text-gray-900">{email}</span>
      </div>
      <button
        type="button"
        onClick={onSignOut}
        class="rounded-md px-1.5 py-0.5 text-xs font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pw-accent)]"
      >
        {t('auth.sign_out', 'Sign out')}
      </button>
    </div>
  );
}

interface FormProps {
  block: AuthPanelBlock;
  allowSignup: boolean;
  allowReset: boolean;
  allowEmailCode: boolean;
  ctx: BlockProps<AuthPanelBlock>['ctx'];
}

function AuthForm({ block, allowSignup, allowReset, allowEmailCode, ctx }: FormProps) {
  const { t } = useI18n();
  const auth = ctx.auth!;
  const providers = block.providers ?? [];

  // initialAuthMode from ctx — the host called openSignup() / openSignin().
  // If the admin disabled signup (allow_signup=false), we ignore 'signup'
  // and start with 'signin' — respecting the admin setting.
  const initial: Mode =
    ctx.initialAuthMode === 'signup' && allowSignup ? 'signup' : 'signin';
  const [mode, setMode] = useState<Mode>(initial);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [busy, setBusy] = useState<null | OAuthProvider | 'email' | 'reset'>(null);
  // Synchronous guard against double-submit: setBusy is an async setState,
  // and two form-submit events in the same tick (Enter+click, double mount
  // in demo-ext, transport race) would both pass `if (busy) return`, firing
  // requestPasswordReset/signIn twice. useRef updates synchronously.
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Sign up — progressive disclosure: the first "Sign Up" click only reveals
  // password+confirm; the second click with filled fields does the real signUp.
  // We reset on mode change — the signin↔signup transition always starts from
  // the collapsed form.
  const [signupExpanded, setSignupExpanded] = useState(false);
  // When an OAuth signin hits identity_already_exists — the chosen Google/Apple
  // account already belongs to an existing user (not the current anon/guest) and
  // the seamless in-popup switch-account retry couldn't complete — we surface a
  // one-tap "sign in to that account" button. The click is a fresh user gesture,
  // so it can open a popup again; switchAccount=true skips linkIdentity.
  const [switchProvider, setSwitchProvider] = useState<OAuthProvider | null>(null);

  // Signup credentials held for the email-confirm auto-resume (see the
  // signup_sent effect below). Memory-only (ref, never storage): the confirm
  // page lives on the paywall's custom domain, so its session can't cross
  // origins into this tab — the only way to continue without re-typing the
  // password is to retry signin with the credentials the user just entered.
  // Cleared on successful signin, on leaving signup_sent, and with the tab.
  const pendingCredsRef = useRef<{ email: string; password: string } | null>(null);

  // Last-used auth method and email (per-paywall). Async-loaded from storage on mount,
  // while null — the UI just renders without the badge. Pre-fill email only if
  // the user hasn't typed anything yet — otherwise we'd overwrite what they're typing.
  //
  // Defensive: old builds of @monetize.software/sdk-extension (≤ 3.0.0-alpha.4)
  // didn't implement getLastLogin in RemoteAuthClient — without the guard the consumer
  // would get `auth.getLastLogin is not a function` in the console. In that case the badge
  // simply isn't shown, and signin keeps working.
  const [lastLogin, setLastLogin] = useState<LastLogin | null>(null);
  useEffect(() => {
    if (typeof auth.getLastLogin !== 'function') return;
    let cancelled = false;
    auth.getLastLogin().then(
      (v) => {
        if (cancelled || !v) return;
        setLastLogin(v);
        if (v.email) {
          setEmail((current) => (current === '' ? v.email! : current));
        }
      },
      () => {
        /* storage unavailable — UI without the badge, signin works */
      }
    );
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const switchTo = (next: Mode): void => {
    setMode(next);
    setError(null);
    setInfo(null);
    setSignupExpanded(false);
    setSwitchProvider(null);
    // A code belongs to the flow that requested it — never carry it into another
    // verify screen, where it would be submitted against a different OTP type.
    setOtpCode('');
    // Leaving signup_sent by hand (Back to sign in) abandons the auto-resume —
    // the user will type the password themselves; don't keep it in memory.
    pendingCredsRef.current = null;
  };

  // Email-confirm auto-resume. While signup_sent is on screen we silently retry
  // signin with the in-memory credentials: on tab focus (the confirm page
  // closes itself after verifying, focus returns here) and on a modest
  // background interval (confirmation may happen on another device). GoTrue
  // rejects with email_not_confirmed until the link is clicked — those (and
  // network hiccups) are swallowed, the view stays as-is. On success SIGNED_IN
  // reaches PaywallRoot via onAuthChange and the auth-resume flow continues the
  // pending checkout by itself. Interval stops after ~10 minutes; focus retries
  // keep working for as long as the view is open.
  useEffect(() => {
    if (mode !== 'signup_sent') return;
    if (!pendingCredsRef.current) return;
    if (typeof window === 'undefined') return;

    let disposed = false;
    let inFlight = false;
    const attempt = async (): Promise<void> => {
      const creds = pendingCredsRef.current;
      if (disposed || inFlight || !creds) return;
      inFlight = true;
      try {
        await auth.signInWithEmail({ email: creds.email, password: creds.password });
        // Signed in — drop the password from memory immediately; the gate
        // advances via onAuthChange.
        pendingCredsRef.current = null;
      } catch {
        /* not confirmed yet / offline — wait for the next signal */
      } finally {
        inFlight = false;
      }
    };

    const onFocus = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void attempt();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    // 20s × 30 ticks ≈ 10 minutes of background polling — enough for the
    // same-device flow many times over, bounded so an abandoned tab doesn't
    // poll the auth endpoint forever.
    let ticks = 0;
    const iv = window.setInterval(() => {
      ticks += 1;
      if (ticks > 30) {
        window.clearInterval(iv);
        return;
      }
      void attempt();
    }, 20_000);

    return () => {
      disposed = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(iv);
    };
  }, [mode, auth]);

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    if (submittingRef.current || busy) return;
    submittingRef.current = true;
    try {
      setError(null);
      setInfo(null);

      // Sign up shortcut: the first submit just reveals the password fields,
      // without a network request. Email is required at this step, otherwise HTML5
      // validation marks the field as required itself.
      if (mode === 'signup' && !signupExpanded) {
        if (!email.trim()) return;
        setSignupExpanded(true);
        return;
      }

      if (mode === 'signup' && password !== confirmPassword) {
        setError(t('auth.passwords_mismatch', "Passwords don't match"));
        return;
      }

      setBusy('email');
      try {
        if (mode === 'signin') {
          await auth.signInWithEmail({ email, password });
        } else if (mode === 'signup') {
          const res = await auth.signUp({ email, password });
          if (res.kind === 'confirmation_required') {
            // Link flow (like recovery): the prod template sends a confirmation link,
            // not a code. We show "check your email → click the link" instead of
            // a dead-end code-entry screen. Confirmation completes at
            // /paywall/v3/auth/confirm on the paywall's custom domain — that
            // session can NOT cross origins into this tab, so the signup_sent
            // effect below retries signin with the credentials kept in memory
            // until the confirmation lands. The password leaves component state
            // either way (ref only).
            pendingCredsRef.current = { email, password };
            setPassword('');
            setMode('signup_sent');
          } else if (res.kind === 'already_registered') {
            // The email is already registered (possibly via Google/Apple). Send
            // the user to sign in instead of a fake "check your email" dead-end —
            // we keep the email prefilled and reveal the signin form (OAuth
            // buttons stay visible above it for the social-login case).
            setMode('signin');
            setSignupExpanded(false);
            setConfirmPassword('');
            setInfo(
              t(
                'auth.email_already_registered',
                'This email is already registered. Sign in below — with your password or the social account you used.'
              )
            );
          }
        } else if (mode === 'forgot') {
          await auth.requestPasswordReset({ email });
          setMode('reset_sent');
        } else if (mode === 'reset_verify') {
          await auth.verifyOtp({
            email,
            token: otpCode,
            type: password ? 'recovery' : 'email'
          });
          if (password) {
            await auth.updatePassword({ password });
          }
        } else if (mode === 'otp') {
          // create_user defaults to true server-side, so one flow covers both
          // sign-in and sign-up. The backend is anti-enumeration (always ok), so
          // we always advance — a non-existent email fails at verify instead.
          await auth.sendOtp({ email });
          setOtpCode('');
          setMode('otp_verify');
        } else if (mode === 'otp_verify') {
          // type 'email' is the signin/signup-by-code variant ('recovery' is the
          // password-reset one). On success setSession + onAuthChange fire, and
          // the auth gate advances on its own — same as a password signin.
          await auth.verifyOtp({ email, token: otpCode, type: 'email' });
        }
      } catch (err) {
        // Signup with email-confirm OFF: GoTrue throws email_exists/user_already_exists
        // (the confirm-ON variant comes back as kind:'already_registered' above).
        // Same UX: send the user to sign in with the email prefilled instead of a
        // bare "account exists" error.
        if (
          mode === 'signup' &&
          err instanceof PaywallError &&
          (err.code === 'email_exists' || err.code === 'user_already_exists')
        ) {
          setMode('signin');
          setSignupExpanded(false);
          setConfirmPassword('');
          setInfo(
            t(
              'auth.email_already_registered',
              'This email is already registered. Sign in below with your password or the account you used to sign up.'
            )
          );
          return;
        }
        const errMode =
          mode === 'signup' ? 'signup'
            : mode === 'reset_verify' || mode === 'otp' || mode === 'otp_verify' ? 'otp'
            : mode === 'forgot' ? 'reset' : 'signin';
        setError(authErrorMessage(err, errMode, t));
      } finally {
        setBusy(null);
      }
    } finally {
      submittingRef.current = false;
    }
  };

  // Resend from the code screen. Shares submittingRef/busy with the form so it
  // can't race a verify in flight. Errors surface here (unlike sendOtp on the
  // email step, which is anti-enumeration and always advances) — at this point
  // the user is already committed to the flow, so a rate-limit needs to be seen.
  const onResendCode = async (): Promise<void> => {
    if (submittingRef.current || busy) return;
    submittingRef.current = true;
    setBusy('email');
    setError(null);
    setInfo(null);
    try {
      await auth.sendOtp({ email });
      setInfo(t('auth.code_resent', 'We sent you a new code.'));
    } catch (err) {
      setError(authErrorMessage(err, 'otp', t));
    } finally {
      submittingRef.current = false;
      setBusy(null);
    }
  };

  const onOAuth = async (
    provider: OAuthProvider,
    opts?: { switchAccount?: boolean }
  ): Promise<void> => {
    if (submittingRef.current || busy) return;
    submittingRef.current = true;
    setBusy(provider);
    setError(null);
    setInfo(null);
    // Clear a stale switch-account prompt on any fresh non-switch attempt.
    if (!opts?.switchAccount) setSwitchProvider(null);
    try {
      await auth.signInWithOAuth({
        provider,
        switchAccount: opts?.switchAccount,
        onPopupOpened: () => setBusy(null),
        // Only the extension SDK acts on this: there the sign-in can outlive
        // this panel (an action popup is destroyed when the provider window
        // takes focus), and offscreen continues the purchase on its own.
        resumeCheckout: ctx.resumeCheckout
      });
      setSwitchProvider(null);
    } catch (err) {
      if (err instanceof PaywallError && (err.code === 'oauth_cancelled' || err.code === 'oauth_timeout')) {
        return;
      }
      // The OAuth identity already belongs to an existing account (the user signed
      // in with it before, on another device). Offer a one-tap sign-in into that
      // account — switchAccount drops the current anon/guest session.
      if (err instanceof PaywallError && err.code === 'oauth_identity_already_linked') {
        setSwitchProvider(provider);
        setError(
          t(
            'auth.identity_already_linked',
            'This account is already registered. Sign in to it below.'
          )
        );
        return;
      }
      // Surface the real cause to the console — the user-facing string is a
      // generic fallback, but merchants debugging a failed OAuth need the actual
      // code/description (e.g. a GoTrue error_description that didn't map to a key).
      if (typeof console !== 'undefined') {
        console.warn('[paywall] OAuth sign-in failed', {
          provider,
          code: err instanceof PaywallError ? err.code : undefined,
          message: err instanceof Error ? err.message : String(err)
        });
      }
      setError(authErrorMessage(err, 'signin', t));
    } finally {
      submittingRef.current = false;
      setBusy(null);
    }
  };

  const showOAuth = providers.length > 0 && (mode === 'signin' || mode === 'signup');
  const showEmailField =
    mode === 'signin' || mode === 'signup' || mode === 'forgot' || mode === 'otp';
  const showPasswordField =
    mode === 'signin' || (mode === 'signup' && signupExpanded);

  if (mode === 'reset_sent') {
    return <ResetSentView email={email} onBack={() => switchTo('signin')} t={t} />;
  }

  if (mode === 'signup_sent') {
    return <SignupSentView email={email} onBack={() => switchTo('signin')} t={t} />;
  }

  return (
    <div class="flex flex-col gap-5">
      <Header mode={mode} customHeading={block.heading} customSubheading={block.subheading} />

      {showOAuth ? (
        <div class="flex flex-col gap-2.5">
          {providers.map((p) => (
            <div key={p} class="relative">
              <button
                type="button"
                onClick={() => onOAuth(p)}
                disabled={busy !== null}
                class="flex h-12 w-full items-center justify-center gap-2.5 rounded-full border-1 border-gray-200 bg-white px-5 text-base font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pw-accent)]"
              >
                {busy === p ? (
                  <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                ) : (
                  <ProviderIcon provider={p} />
                )}
                <span>{providerLabel(p, t)}</span>
              </button>
              {lastLogin?.method === p ? <LastUsedBadge email={lastLogin.email} /> : null}
            </div>
          ))}
          <Divider />
        </div>
      ) : null}

      <form onSubmit={onSubmit} class="flex flex-col gap-3">
        {showEmailField && (
          <FilledField
            type="email"
            placeholder={t('auth.email', 'Email address')}
            value={email}
            onInput={setEmail}
            autocomplete="email"
            required
          />
        )}

        {showPasswordField && (
          <PasswordField
            placeholder={t('auth.password', 'Password')}
            value={password}
            onInput={setPassword}
            autocomplete={mode === 'signin' ? 'current-password' : 'new-password'}
            required
          />
        )}

        {mode === 'signup' && signupExpanded && (
          <PasswordField
            placeholder={t('auth.repeat_password', 'Repeat password')}
            value={confirmPassword}
            onInput={setConfirmPassword}
            autocomplete="new-password"
            required
          />
        )}

        {(mode === 'reset_verify' || mode === 'otp_verify') && (
          <FilledField
            type="text"
            placeholder={t('auth.confirmation_code', 'Confirmation code')}
            value={otpCode}
            onInput={setOtpCode}
            autocomplete="one-time-code"
            inputMode="numeric"
            required
          />
        )}

        {mode === 'reset_verify' && (
          <PasswordField
            placeholder={t(
              'auth.new_password_optional',
              'New password (optional — only for password reset)'
            )}
            value={password}
            onInput={setPassword}
            autocomplete="new-password"
          />
        )}

        {(mode === 'signin' || mode === 'signup') &&
          (allowEmailCode || (mode === 'signin' && allowReset)) && (
            <div class="flex items-center justify-between gap-3 text-sm">
              {allowEmailCode ? (
                <AccentLink onClick={() => switchTo('otp')}>
                  {t('auth.use_email_code', 'Sign in with a code')}
                </AccentLink>
              ) : (
                <span />
              )}
              {mode === 'signin' && allowReset ? (
                <AccentLink onClick={() => switchTo('forgot')}>
                  {t('auth.forgot_password', 'Forgot password?')}
                </AccentLink>
              ) : null}
            </div>
          )}

        {mode === 'otp_verify' && (
          <div class="flex justify-start text-sm">
            <AccentLink onClick={onResendCode}>
              {t('auth.resend_code', 'Send the code again')}
            </AccentLink>
          </div>
        )}

        {error && <p class="text-sm text-red-600">{error}</p>}
        {info && <p class="text-sm text-gray-500">{info}</p>}

        {switchProvider && (
          <button
            type="button"
            onClick={() => onOAuth(switchProvider, { switchAccount: true })}
            disabled={busy !== null}
            class="flex h-12 w-full items-center justify-center gap-2.5 rounded-full border-1 border-gray-200 bg-white px-5 text-base font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pw-accent)]"
          >
            {busy === switchProvider ? (
              <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
            ) : (
              <ProviderIcon provider={switchProvider} />
            )}
            <span>{providerLabel(switchProvider, t)}</span>
          </button>
        )}

        <PrimaryButton
          busy={busy === 'email'}
          label={submitLabel(mode, signupExpanded, block.submit_label ?? block.heading, t)}
        />
      </form>

      <FormFooter
        mode={mode}
        allowSignup={allowSignup}
        onSwitch={switchTo}
      />
    </div>
  );
}

function Header({
  mode,
  customHeading,
  customSubheading
}: {
  mode: Mode;
  customHeading?: string | null;
  customSubheading?: string | null;
}) {
  const { t } = useI18n();
  // customHeading/customSubheading override the default for the signin+signup modes.
  // A restore/preauth intent sets its own heading, but when the user clicks
  // "Forgot password?" — the view changes to forgot and must show
  // the default "Forgot password?" title, not the intent-specific string.
  // Reset views (forgot/reset_sent/reset_verify) always use the defaults.
  const defaults = defaultHeader(mode, t);
  const useCustom = mode === 'signin' || mode === 'signup';
  const title = useCustom && customHeading ? customHeading : defaults.title;
  const subtitle =
    useCustom && customSubheading !== undefined
      ? customSubheading || null
      : defaults.subtitle;
  return (
    <div class="flex flex-col gap-2">
      <h2 class="text-3xl font-bold tracking-tight text-gray-900">{title}</h2>
      {subtitle ? (
        <p class="text-base leading-relaxed text-gray-600">{subtitle}</p>
      ) : null}
    </div>
  );
}

function defaultHeader(mode: Mode, t: TFn): { title: string; subtitle: string | null } {
  switch (mode) {
    case 'signin':
      return {
        title: t('auth.welcome', 'Welcome back!'),
        subtitle: t('auth.default_subtitle', 'Sign in to access all features and sync your data.')
      };
    case 'signup':
      return {
        title: t('auth.welcome_signup', 'Welcome!'),
        subtitle: t('auth.default_subtitle', 'Sign in to access all features and sync your data.')
      };
    case 'forgot':
      return {
        title: t('auth.forgot_password_title', 'Forgot password?'),
        subtitle: t(
          'auth.forgot_subtitle',
          "Enter your email and we'll send you a password reset link."
        )
      };
    case 'reset_sent':
    case 'signup_sent':
      return {
        title: t('auth.check_email_title', 'Check your email'),
        subtitle: null
      };
    case 'reset_verify':
      return {
        title: t('auth.reset_password_title', 'Reset password'),
        subtitle: t(
          'auth.reset_password_subtitle',
          'Enter the code from your email and a new password.'
        )
      };
    case 'otp':
      return {
        title: t('auth.otp_title', 'Sign in with a code'),
        subtitle: t(
          'auth.otp_subtitle',
          "Enter your email and we'll send you a sign-in code — no password needed."
        )
      };
    case 'otp_verify':
      return {
        title: t('auth.otp_verify_title', 'Enter the code'),
        subtitle: t(
          'auth.otp_verify_subtitle',
          'We sent a 6-digit code to your email. Enter it here to sign in.'
        )
      };
  }
}

function submitLabel(
  mode: Mode,
  signupExpanded: boolean,
  customHeading: string | undefined,
  t: TFn
): string {
  // If customHeading is set — it's also used as the submit label for signin
  // ("Restore Purchases" → button "Restore Purchases"). For the other modes
  // the submit label is fixed (Sign Up / Send Reset Email / Verify).
  if (mode === 'signin' && customHeading) return customHeading;
  switch (mode) {
    case 'signin':
      return t('auth.log_in', 'Sign In');
    case 'signup':
      return signupExpanded
        ? t('auth.create_account', 'Create Account')
        : t('auth.sign_up', 'Sign Up');
    case 'forgot':
      return t('auth.send_reset', 'Send Reset Email');
    case 'otp':
      return t('auth.send_code', 'Send Code');
    case 'reset_verify':
    case 'otp_verify':
      return t('auth.verify', 'Verify');
    default:
      return t('cta.continue', 'Continue');
  }
}

function FormFooter({
  mode,
  allowSignup,
  onSwitch
}: {
  mode: Mode;
  allowSignup: boolean;
  onSwitch: (m: Mode) => void;
}) {
  const { t } = useI18n();
  if (mode === 'signin' && allowSignup) {
    return (
      <p class="text-center text-sm text-gray-600">
        {t('auth.no_account', "Don't have an account?")}{' '}
        <AccentLink onClick={() => onSwitch('signup')}>
          {t('auth.sign_up_link', 'Sign Up')}
        </AccentLink>
      </p>
    );
  }
  if (mode === 'signup') {
    return (
      <p class="text-center text-sm text-gray-600">
        {t('auth.have_account', 'Already have an account?')}{' '}
        <AccentLink onClick={() => onSwitch('signin')}>
          {t('auth.log_in_link', 'Log In')}
        </AccentLink>
      </p>
    );
  }
  if (mode === 'forgot' || mode === 'reset_sent' || mode === 'reset_verify') {
    return (
      <p class="text-center text-sm text-gray-600">
        {t('auth.no_account', "Don't have an account?")}{' '}
        <AccentLink onClick={() => onSwitch('signup')}>
          {t('auth.sign_up_link', 'Sign Up')}
        </AccentLink>
      </p>
    );
  }
  // The code flow signs up and signs in alike, so there's no "no account?"
  // branch to offer here — only the way back to the password form.
  if (mode === 'otp' || mode === 'otp_verify') {
    return (
      <p class="text-center text-sm text-gray-600">
        <AccentLink onClick={() => onSwitch('signin')}>
          {t('auth.back_to_login', 'Back to Login')}
        </AccentLink>
      </p>
    );
  }
  return null;
}

function AccentLink({
  onClick,
  children
}: {
  onClick: () => void;
  children: preact.ComponentChildren;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class="font-semibold transition-opacity hover:opacity-80 focus:outline-none focus-visible:opacity-80"
      style={{ color: 'var(--pw-accent)' }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ busy, label }: { busy: boolean; label: string }) {
  return (
    <button
      type="submit"
      disabled={busy}
      class="pw-cta-shimmer relative mt-1 flex min-h-12 w-full items-center justify-center overflow-hidden rounded-3xl px-5 py-2 text-center text-base font-semibold leading-tight text-white transition-transform duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--pw-accent)]"
      style={{
        background:
          'linear-gradient(135deg, color-mix(in srgb, var(--pw-accent) 55%, white) 0%, var(--pw-accent) 55%, color-mix(in srgb, var(--pw-accent) 90%, black) 100%)',
        boxShadow:
          '0 0 20px 0 color-mix(in srgb, var(--pw-accent) 25%, transparent), inset 0 0 8px 0 color-mix(in srgb, white 25%, transparent)'
      }}
    >
      {busy ? (
        <span class="relative z-10 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      ) : (
        <span class="relative z-10">{label}</span>
      )}
    </button>
  );
}

interface FilledFieldProps {
  type: 'email' | 'text';
  placeholder: string;
  value: string;
  onInput: (v: string) => void;
  autocomplete?: string;
  inputMode?: 'numeric' | 'text' | 'email';
  required?: boolean;
}

function FilledField({ type, placeholder, value, onInput, autocomplete, inputMode, required }: FilledFieldProps) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      autocomplete={autocomplete}
      inputMode={inputMode}
      required={required}
      class="h-14 w-full rounded-2xl bg-gray-100 px-5 text-base text-gray-900 outline-none transition-all placeholder:text-gray-500 hover:bg-gray-200/60 focus:bg-gray-200/60 focus:shadow-[0_0_0_2px_color-mix(in_srgb,var(--pw-accent)_30%,transparent)]"
    />
  );
}

interface PasswordFieldProps {
  placeholder: string;
  value: string;
  onInput: (v: string) => void;
  autocomplete?: string;
  required?: boolean;
}

function PasswordField({ placeholder, value, onInput, autocomplete, required }: PasswordFieldProps) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Chrome/Safari clear .value when the type switches between password↔text (autofill-guard).
  // Preact sees the same value prop and doesn't re-set the DOM — the field stays empty.
  useEffect(() => {
    const el = inputRef.current;
    if (el && el.value !== value) el.value = value;
  }, [visible, value]);
  const passwordAriaShow = t('auth.show_password', 'Show password');
  const passwordAriaHide = t('auth.hide_password', 'Hide password');
  return (
    <div class="relative">
      <input
        ref={inputRef}
        type={visible ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        onInput={(e) => onInput((e.target as HTMLInputElement).value)}
        autocomplete={autocomplete}
        required={required}
        class="h-14 w-full rounded-2xl bg-gray-100 pl-5 pr-12 text-base text-gray-900 outline-none transition-all placeholder:text-gray-500 hover:bg-gray-200/60 focus:bg-gray-200/60 focus:shadow-[0_0_0_2px_color-mix(in_srgb,var(--pw-accent)_30%,transparent)]"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? passwordAriaHide : passwordAriaShow}
        tabIndex={-1}
        class="absolute right-4 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pw-accent)]"
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M1.667 10S4.583 4.167 10 4.167 18.333 10 18.333 10 15.417 15.833 10 15.833 1.667 10 1.667 10Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" stroke-width="1.5" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M8.236 4.293A6.96 6.96 0 0 1 10 4.167C15.417 4.167 18.333 10 18.333 10a13.5 13.5 0 0 1-1.92 2.755M11.768 11.768A2.5 2.5 0 0 1 8.233 8.233"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14.953 14.953A8.84 8.84 0 0 1 10 15.833C4.583 15.833 1.667 10 1.667 10a13.5 13.5 0 0 1 3.38-3.953M1.667 1.667l16.666 16.666"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function LastUsedBadge({ email }: { email: string | null }) {
  const { t } = useI18n();
  // A pill in the top-right corner of the button. truncate + max-w guard against a long
  // email (which would overflow the button edges). pointer-events-none — so the click lands
  // on the button itself, not on the badge on top.
  const label = email
    ? t('auth.last_used', 'Last used · {email}', { email: maskEmail(email) })
    : t('auth.last_used_no_email', 'Last used');
  return (
    <span class="pointer-events-none absolute -top-2 right-3 max-w-[75%] truncate rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-white shadow-sm">
      {label}
    </span>
  );
}

// alex@example.com → ale*****@example.com. We mask the local part (the
// first 3 characters are visible) and leave the domain as is — it's public and helps
// the user recognize the account.
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const visible = local.slice(0, 3);
  return `${visible}*****@${domain}`;
}

function Divider() {
  const { t } = useI18n();
  return (
    <div class="flex items-center gap-3 py-1 text-sm text-gray-400">
      <div class="h-px flex-1 bg-gray-200" />
      <span>{t('auth.or', 'or')}</span>
      <div class="h-px flex-1 bg-gray-200" />
    </div>
  );
}

function ProviderIcon({ provider }: { provider: OAuthProvider }) {
  if (provider === 'google') {
    return (
      <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62Z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.36 0-4.36-1.59-5.07-3.74H.92v2.33A9 9 0 0 0 9 18Z" />
        <path fill="#FBBC05" d="M3.93 10.68a5.4 5.4 0 0 1 0-3.36V4.99H.92a9 9 0 0 0 0 8.02l3-2.33Z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A9 9 0 0 0 .92 4.99l3.01 2.33C4.64 5.17 6.64 3.58 9 3.58Z" />
      </svg>
    );
  }
  if (provider === 'apple') {
    return (
      // viewBox 0 0 24 24 leaves whitespace above/below the path, so visually
      // the Apple logo looks smaller than Google. We compensate with a larger
      // width/height — 26×26 gives roughly equal optical size to Google's 20×20.
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
    );
  }
  if (provider === 'github') {
    return (
      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 0C3.6 0 0 3.6 0 8a8 8 0 0 0 5.5 7.6c.4.1.5-.2.5-.4v-1.5c-2.2.5-2.7-1-2.7-1-.4-.9-.9-1.2-.9-1.2-.7-.5.1-.5.1-.5.8.1 1.2.8 1.2.8.7 1.2 1.9.9 2.4.7 0-.5.3-.9.5-1.1-1.8-.2-3.6-.9-3.6-4 0-.9.3-1.6.8-2.1-.1-.2-.4-1 .1-2.1 0 0 .7-.2 2.2.8a7.6 7.6 0 0 1 4 0c1.5-1 2.2-.8 2.2-.8.4 1.1.2 1.9.1 2.1.5.5.8 1.2.8 2.1 0 3.1-1.9 3.7-3.6 3.9.3.3.6.8.6 1.6V15c0 .2.1.5.6.4A8 8 0 0 0 16 8c0-4.4-3.6-8-8-8Z" />
      </svg>
    );
  }
  return (
    <svg width="18" height="20" viewBox="0 0 14 16" fill="currentColor" aria-hidden="true">
      <path d="M14 2.7C14 1.2 12.8 0 11.3 0H2.7C1.2 0 0 1.2 0 2.7v10.6C0 14.8 1.2 16 2.7 16h4V9.8H4.7v-2H6.7V6.4c0-2 1.2-3.1 3-3.1.9 0 1.7.1 2 .2V5h-1.4c-.8 0-1 .4-1 1v1.5h2.4l-.3 2H9.3V16h2c1.5 0 2.7-1.2 2.7-2.7V2.7Z" />
    </svg>
  );
}

// The signup confirmation link flow — a mirror of ResetSentView. The prod "Confirm signup"
// email template sends a link (redirect_to → /paywall/v3/auth/confirm), not a code.
// After clicking the link the user is confirmed on the v3 page, the session syncs
// cross-tab → the auth gate advances by itself. This screen is "awaiting confirmation"
// + a "Back to Login" fallback (email already confirmed → can sign in with a password).
function SignupSentView({
  email,
  onBack,
  t
}: {
  email: string;
  onBack: () => void;
  t: TFn;
}) {
  return (
    <div class="flex flex-col items-center gap-4 py-2 text-center">
      <div
        class="flex h-14 w-14 items-center justify-center rounded-full"
        style={{
          background: 'linear-gradient(135deg, #4ade80, #16a34a)',
          color: '#fff',
          boxShadow:
            '0 0 0 8px rgba(74,222,128,0.12), 0 8px 20px -6px rgba(22,163,74,0.45)'
        }}
        aria-hidden="true"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 class="mt-1 text-3xl font-bold tracking-tight text-gray-900">
        {t('auth.check_email_title', 'Check your email')}
      </h2>

      <p class="text-base leading-relaxed text-gray-600">
        {t(
          'auth.signup_sent_subtitle',
          'We sent a confirmation link to your email. Click it to activate your account — you will be signed in here automatically.'
        )}
      </p>

      {email ? (
        <p class="break-all text-base font-semibold text-gray-900">{email}</p>
      ) : null}

      <button
        type="button"
        onClick={onBack}
        class="pw-cta-shimmer relative mt-2 flex min-h-12 w-full items-center justify-center overflow-hidden rounded-3xl px-5 py-2 text-center text-base font-semibold leading-tight text-white transition-transform duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--pw-accent)]"
        style={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--pw-accent) 55%, white) 0%, var(--pw-accent) 55%, color-mix(in srgb, var(--pw-accent) 90%, black) 100%)',
          boxShadow:
            '0 0 20px 0 color-mix(in srgb, var(--pw-accent) 25%, transparent), inset 0 0 8px 0 color-mix(in srgb, white 25%, transparent)'
        }}
      >
        <span class="relative z-10">
          {t('auth.back_to_login', 'Back to Login')}
        </span>
      </button>
    </div>
  );
}

function ResetSentView({
  email,
  onBack,
  t
}: {
  email: string;
  onBack: () => void;
  t: TFn;
}) {
  return (
    <div class="flex flex-col items-center gap-4 py-2 text-center">
      <div
        class="flex h-14 w-14 items-center justify-center rounded-full"
        style={{
          background: 'linear-gradient(135deg, #4ade80, #16a34a)',
          color: '#fff',
          boxShadow:
            '0 0 0 8px rgba(74,222,128,0.12), 0 8px 20px -6px rgba(22,163,74,0.45)'
        }}
        aria-hidden="true"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 class="mt-1 text-3xl font-bold tracking-tight text-gray-900">
        {t('auth.check_email_title', 'Check your email')}
      </h2>

      <p class="text-base leading-relaxed text-gray-600">
        {t(
          'auth.reset_sent_subtitle',
          'We sent a password reset link. Follow the instructions in the email to reset your password.'
        )}
      </p>

      {email ? (
        <p class="break-all text-base font-semibold text-gray-900">{email}</p>
      ) : null}

      <p class="text-sm text-gray-500">
        {t('auth.reset_link_valid', 'The link is valid for 1 hour.')}
      </p>

      <button
        type="button"
        onClick={onBack}
        class="pw-cta-shimmer relative mt-2 flex min-h-12 w-full items-center justify-center overflow-hidden rounded-3xl px-5 py-2 text-center text-base font-semibold leading-tight text-white transition-transform duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--pw-accent)]"
        style={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--pw-accent) 55%, white) 0%, var(--pw-accent) 55%, color-mix(in srgb, var(--pw-accent) 90%, black) 100%)',
          boxShadow:
            '0 0 20px 0 color-mix(in srgb, var(--pw-accent) 25%, transparent), inset 0 0 8px 0 color-mix(in srgb, white 25%, transparent)'
        }}
      >
        <span class="relative z-10">
          {t('auth.back_to_login', 'Back to Login')}
        </span>
      </button>
    </div>
  );
}
